import { computed, markRaw, readonly, ref } from 'vue'
import { ExternalToast, toast } from 'vue-sonner'
import AppNotification from '~/components/AppNotification.vue'
import { truncate } from '~/helper'
import { addMissingTimezoneUTC } from '~/helper/date'
import dayjs from '~/lib/dayjs'
import type { WSProgressNotification } from '~/api/websocket'
import { formatDuration } from '~/helper/duration'

type AppNotifyAction = {
  label: string
  onClick: () => void
}
export type AppNotifyTypes = 'error' | 'warning' | 'success' | 'info'
export type AppNotifyOptions = {
  type?: AppNotifyTypes
  action?: AppNotifyAction
  /** Change this type if a different lib or underlying implementation is used */
  lib?: ExternalToast
}
type NotifyOptionsPreset = Omit<AppNotifyOptions, 'type'>

export type AppProgressNotificationRecord = WSProgressNotification & {
  updated_at: string
}

const AppNotificationCircles: { [key in AppNotifyTypes]: string } & {
  default: string
} = {
  error: '🔴',
  warning: '🟡',
  success: '🟢',
  info: '🔵',
  default: '⚪',
}

const PROGRESS_OVERLAY_DISMISS_DELAY_MS = 3000
const appProgressNotifications = ref<AppProgressNotificationRecord[]>([])
const dismissedProgressNotificationIds = ref<string[]>([])
const progressDismissTimeouts = new Map<string, ReturnType<typeof setTimeout>>()
const progressDismissStartedAt = new Map<string, number>()
const progressDismissRemainingMs = new Map<string, number>()
const progressDismissHolds = new Map<string, Set<ProgressDismissHoldReason>>()

type ProgressDismissHoldReason = 'hover' | 'focus'

type ProgressItemLike = Pick<
  AppProgressNotificationRecord,
  'state' | 'current' | 'total' | 'percent' | 'created_at' | 'completed_at'
>

/**
 * How the notification system works:
 * - current underlying implementation is "vue-sonner" (interface-like, can be changed in the future)
 * - "vue-sonner" uses pub-sub to trigger a toast
 * - <Toaster> in app.vue is subscribed to this pub-sub
 * - <Toaster> wraps/uses our <AppNotification>
 */
function notifyFn(message: string, options?: AppNotifyOptions) {
  // Merge internal options with lib options
  // currently using https://vue-sonner.vercel.app/
  const mergedOptions: AppNotifyOptions['lib'] = {
    ...options?.lib,
    component: markRaw(AppNotification),
    componentProps: {
      message,
      options,
    },
  }
  if (options?.action) {
    mergedOptions.action = {
      label: options.action.label,
      onClick: options.action.onClick,
    }
  }

  // Display console notifications when on dev (to find trace/origin)
  if (import.meta.dev) {
    const circle = AppNotificationCircles[options?.type ?? 'default']
    console.groupCollapsed(
      `${circle} %c Notification trace:` + `%c ${truncate(message, 25, true)}`,
      'background: #ffeb78; cursor: pointer; color: black',
      'font-weight: normal; cursor: pointer; color: #ababab;',
    )
    console.log(`%c Message: %c${message}`, 'color: #ababab;', 'font-weight: bold;')
    console.trace()
    console.groupEnd()
  }

  if (!options?.type) {
    return toast(message, mergedOptions)
  } else if (options.type == 'error') {
    return toast.error(message, mergedOptions)
  } else if (options.type == 'warning') {
    return toast.warning(message, mergedOptions)
  } else if (options.type == 'success') {
    return toast.success(message, mergedOptions)
  } else if (options.type == 'info') {
    return toast.info(message, mergedOptions)
  } else {
    return toast(message, mergedOptions)
  }
}

export function isCompletedProgress(item: Pick<ProgressItemLike, 'current' | 'total' | 'percent' | 'completed_at'>) {
  return Boolean(item.completed_at) || item.percent >= 100 || (item.total > 0 && item.current >= item.total)
}

export function getProgressStateColor(
  item: Pick<ProgressItemLike, 'state' | 'current' | 'total' | 'percent' | 'completed_at'>,
) {
  const normalizedState = item.state.toLowerCase()
  if (isCompletedProgress(item) || ['completed', 'done', 'success', 'finished'].includes(normalizedState)) {
    return 'success'
  }
  if (['error', 'failed', 'failure'].includes(normalizedState)) {
    return 'error'
  }
  if (['started', 'running', 'in_progress', 'queued', 'progress'].includes(normalizedState)) {
    return 'info'
  }
  return 'info'
}

export function getProgressStateIcon(
  item: Pick<ProgressItemLike, 'state' | 'current' | 'total' | 'percent' | 'completed_at'>,
) {
  const normalizedState = item.state.toLowerCase()
  if (isCompletedProgress(item) || ['completed', 'done', 'success', 'finished'].includes(normalizedState)) {
    return 'mdi-check-circle'
  }
  if (['error', 'failed', 'failure'].includes(normalizedState)) {
    return 'mdi-alert-circle'
  }
  if (['started', 'running', 'in_progress', 'queued', 'progress'].includes(normalizedState)) {
    return 'mdi-timer-sand'
  }
  return 'mdi-progress-question'
}

export function getProgressStateLabel(state: string) {
  return state.replaceAll('_', ' ')
}

export function getProgressValueLabel(
  item: Pick<ProgressItemLike, 'current' | 'total' | 'percent'>,
  includePercent = false,
) {
  if (item.total > 0) {
    return includePercent ? `${item.current} / ${item.total} (${item.percent}%)` : `${item.current} / ${item.total}`
  }
  return `${item.percent}%`
}

export function getProgressRuntimeLabel(
  item: Pick<ProgressItemLike, 'created_at' | 'completed_at'>,
  now: Date | string | number = new Date(),
) {
  const startedAt = dayjs(item.created_at)
  if (!startedAt.isValid()) {
    return '—'
  }

  const endDate = item.completed_at ? dayjs(item.completed_at) : dayjs(now)
  if (!endDate.isValid()) {
    return '—'
  }

  return formatDuration(endDate.diff(startedAt))
}

function startProgressDismissTimer(id: string) {
  const remainingMs = progressDismissRemainingMs.get(id)
  if (remainingMs === undefined || hasProgressDismissHold(id)) {
    return
  }

  clearProgressDismissTimer(id)
  progressDismissStartedAt.set(id, Date.now())
  progressDismissTimeouts.set(
    id,
    setTimeout(() => {
      resetProgressDismissState(id)
      Progress.dismiss(id)
    }, remainingMs),
  )
}

function scheduleProgressDismiss(id: string, delayMs = PROGRESS_OVERLAY_DISMISS_DELAY_MS) {
  progressDismissRemainingMs.set(id, Math.max(0, delayMs))
  startProgressDismissTimer(id)
}

function clearProgressDismissTimer(id: string) {
  const timeout = progressDismissTimeouts.get(id)
  if (!timeout) {
    return
  }
  clearTimeout(timeout)
  progressDismissTimeouts.delete(id)
  progressDismissStartedAt.delete(id)
}

function resetProgressDismissState(id: string, preserveHold = false) {
  clearProgressDismissTimer(id)
  progressDismissRemainingMs.delete(id)
  if (!preserveHold) {
    progressDismissHolds.delete(id)
  }
}

function clearAllProgressDismissStates() {
  for (const id of progressDismissTimeouts.keys()) {
    clearProgressDismissTimer(id)
  }
  progressDismissRemainingMs.clear()
  progressDismissHolds.clear()
}

function hasProgressDismissHold(id: string) {
  return Boolean(progressDismissHolds.get(id)?.size)
}

function setProgressDismissHold(id: string, reason: ProgressDismissHoldReason, active: boolean) {
  if (active) {
    const holds = progressDismissHolds.get(id) ?? new Set<ProgressDismissHoldReason>()
    holds.add(reason)
    progressDismissHolds.set(id, holds)
    return
  }

  const holds = progressDismissHolds.get(id)
  if (!holds) {
    return
  }

  holds.delete(reason)
  if (!holds.size) {
    progressDismissHolds.delete(id)
  }
}

function pauseProgressDismissFn(id: string, reason: ProgressDismissHoldReason = 'hover') {
  setProgressDismissHold(id, reason, true)

  const startedAt = progressDismissStartedAt.get(id)
  if (startedAt === undefined) {
    return
  }

  const remainingMs = progressDismissRemainingMs.get(id) ?? PROGRESS_OVERLAY_DISMISS_DELAY_MS
  const elapsedMs = Date.now() - startedAt

  progressDismissRemainingMs.set(id, Math.max(0, remainingMs - elapsedMs))
  clearProgressDismissTimer(id)
}

function resumeProgressDismissFn(id: string, reason: ProgressDismissHoldReason = 'hover') {
  setProgressDismissHold(id, reason, false)
  startProgressDismissTimer(id)
}

/**
 * Spawn or update an existing progress Notification
 */
function progressUpsert(notification: WSProgressNotification) {
  const normalizedEntry: AppProgressNotificationRecord = {
    ...notification,
    current: Number.isFinite(notification.current) ? Math.max(0, notification.current) : 0,
    total: Number.isFinite(notification.total) ? Math.max(0, notification.total) : 0,
    percent: Number.isFinite(notification.percent) ? Math.min(100, Math.max(0, notification.percent)) : 0,
    created_at: addMissingTimezoneUTC(notification.created_at) || '',
    completed_at: addMissingTimezoneUTC(notification.completed_at) || null,
    updated_at: new Date().toISOString(),
  }

  const index = appProgressNotifications.value.findIndex((item) => item.id === normalizedEntry.id)
  if (index === -1) {
    appProgressNotifications.value.unshift(normalizedEntry)
  } else {
    appProgressNotifications.value[index] = normalizedEntry
  }

  if (isCompletedProgress(normalizedEntry)) {
    scheduleProgressDismiss(normalizedEntry.id)
  } else {
    resetProgressDismissState(normalizedEntry.id, true)
  }
}

function progressFn(
  id: string,
  label: string,
  step: string | null = null,
  state: string,
  current: number,
  total: number,
  percent: number,
  created_at: string,
  completed_at: string | null = null,
) {
  progressUpsert({
    id,
    label,
    step,
    state,
    current,
    total,
    percent,
    created_at,
    completed_at,
  })
}

function removeProgressFn(id: string) {
  resetProgressDismissState(id)
  dismissedProgressNotificationIds.value = dismissedProgressNotificationIds.value.filter(
    (dismissedId) => dismissedId !== id,
  )
  appProgressNotifications.value = appProgressNotifications.value.filter((item) => item.id !== id)
}

function dismissProgressFn(id: string) {
  if (dismissedProgressNotificationIds.value.includes(id)) {
    return
  }
  resetProgressDismissState(id)
  dismissedProgressNotificationIds.value = [
    ...dismissedProgressNotificationIds.value,
    id,
  ]
}

function closeProgressFn(id: string) {
  dismissProgressFn(id)
}

function clearProgressFn() {
  clearAllProgressDismissStates()
  dismissedProgressNotificationIds.value = []
  appProgressNotifications.value = []
}

export function useProgressNotifications() {
  return computed(() => readonly(appProgressNotifications.value))
}

export function useVisibleProgressNotifications() {
  return computed(() =>
    readonly(
      appProgressNotifications.value.filter((item) => !dismissedProgressNotificationIds.value.includes(item.id)),
    ),
  )
}

/**
 * Expose Notify with helper Functions
 */
export const Notify = Object.assign(notifyFn, {
  error: (message: string, opt?: NotifyOptionsPreset) => notifyFn(message, { ...opt, type: 'error' }),
  warning: (message: string, opt?: NotifyOptionsPreset) => notifyFn(message, { ...opt, type: 'warning' }),
  success: (message: string, opt?: NotifyOptionsPreset) => notifyFn(message, { ...opt, type: 'success' }),
  info: (message: string, opt?: NotifyOptionsPreset) => notifyFn(message, { ...opt, type: 'info' }),
})

export const Progress = Object.assign(progressFn, {
  from: progressUpsert,
  dismiss: dismissProgressFn,
  remove: removeProgressFn,
  close: closeProgressFn,
  clear: clearProgressFn,
  pauseDismiss: pauseProgressDismissFn,
  resumeDismiss: resumeProgressDismissFn,
})

export function mapNotifyType(type: string): AppNotifyTypes {
  if (type == 'error') {
    return 'error'
  } else if (type == 'warning') {
    return 'warning'
  } else if (type == 'success') {
    return 'success'
  } else {
    return 'info'
  }
}
